import { CheckCircleOutline, WarningAmberRounded } from "@mui/icons-material";
import { Box, Divider, Switch, Typography, styled } from "@mui/material";

type ProtectionToggleRowProps = {
  /** Kept stable across states so e2e selectors and analytics keep working. */
  id: string;
  /** Name of the protection, without any state wording, e.g. "Delete Protection". */
  label: string;
  /** True when the protection is currently on. */
  isProtected: boolean;
  onToggle: () => void;
  disabled?: boolean;
};

const TOGGLE_COLOR = "#1976d2";

const labelStyle = {
  display: "inline-block",
  width: 160,
  fontWeight: "500",
  whiteSpace: "nowrap",
};

/** Short enough to read as an inline separator rather than a column rule. */
const DIVIDER_HEIGHT = 28;

/**
 * MUI's recommended customization recipe (mui.com/material-ui/react-switch/#customization),
 * themed to the app's existing toggle color instead of iOS green.
 */
const ProtectionSwitch = styled(Switch)(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "150ms",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: TOGGLE_COLOR,
        opacity: 1,
        border: 0,
      },
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: 0.5,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 22,
    height: 22,
  },
  "& .MuiSwitch-track": {
    borderRadius: 13,
    backgroundColor: theme.palette.grey[400],
    opacity: 1,
  },
}));

/**
 * Renders a protection toggle as "<name> | <state>" so the toggle position and
 * the wording can never be read as a double negative. The label names the
 * protection, the value after the divider is its current state.
 */
export function ProtectionToggleRow({
  id,
  label,
  isProtected,
  onToggle,
  disabled = false,
}: ProtectionToggleRowProps) {
  const stateLabel = isProtected ? "Enabled" : "Disabled";
  // An unprotected cluster is the risky state, so it reads as a warning rather
  // than as neutral "off": red triangle, red text.
  const StateIcon = isProtected ? CheckCircleOutline : WarningAmberRounded;
  const iconColor = isProtected ? "success.main" : "error.main";
  const textColor = isProtected ? "success.main" : "error.main";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
      }}
    >
      <ProtectionSwitch
        checked={isProtected}
        disabled={disabled}
        onChange={onToggle}
        sx={{ marginRight: 1 }}
        inputProps={{
          id,
          "aria-label": `${label} ${stateLabel}. Click to ${
            isProtected ? "disable" : "enable"
          } ${label}.`,
        }}
      />
      <Typography component="span" sx={labelStyle}>
        {label}
      </Typography>
      <Divider
        orientation="vertical"
        data-testid={`${id}-divider`}
        sx={{ height: DIVIDER_HEIGHT, alignSelf: "center", marginX: 2 }}
      />
      <Box
        id={`${id}-state`}
        data-testid={`${id}-state`}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
        }}
      >
        <StateIcon fontSize="small" sx={{ color: iconColor }} />
        <Typography
          component="span"
          sx={{ fontWeight: "500", color: textColor }}
        >
          {stateLabel}
        </Typography>
      </Box>
    </Box>
  );
}

export default ProtectionToggleRow;
